import {
  Component,
  ComponentRef,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { ColorPickerPanelComponent } from './color-picker-panel.component';
import { GradientType, isGradient, parseColorValue, parseHex } from './color-value';

/**
 * Farbfeld: Farbtupfer + Hex-Feld, Klick auf den Tupfer öffnet die eigene
 * Farbauswahl (`app-color-picker-panel`) als Popover im CDK-Overlay — der
 * Container hängt dank Nebular in `nb-layout` (CLAUDE.md Regel 12), dort gelten
 * CI-Tokens und die Overlay-Z-Index-Staffel aus styles.scss.
 *
 * Wert (CVA) = String nach `color-value.ts`: `#rrggbb[aa]` oder — nur mit
 * `[allowGradient]` — ein `linear-/radial-/conic-gradient(…)`.
 */
@Component({
  selector: 'app-color-input',
  templateUrl: './color-input.component.html',
  styleUrl: './color-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorInputComponent),
      multi: true,
    },
  ],
})
export class ColorInputComponent implements ControlValueAccessor, OnDestroy {
  /** Erlaubt den Reiter „Verlauf“. Schriftfarben bleiben einfarbig. */
  @Input() allowGradient = false;

  @ViewChild('anchor', { static: true }) anchor!: ElementRef<HTMLElement>;
  @ViewChild('swatch', { static: true }) swatch!: ElementRef<HTMLButtonElement>;

  value: string = '#000000';
  hexInput: string = '#000000';
  isDisabled = false;
  /** Verlaufsart, solange der Wert ein Verlauf ist (für das Etikett). */
  gradientType: GradientType | null = null;
  open = false;

  private overlayRef?: OverlayRef;
  private panelRef?: ComponentRef<ColorPickerPanelComponent>;
  private overlaySubs = new Subscription();

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private host: ElementRef<HTMLElement>
  ) {}

  writeValue(val: string): void {
    if (val) {
      this.setValue(val);
      this.panelRef?.setInput('value', val);
    }
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.closePicker(false);
  }

  /** Name bleibt für bestehende Aufrufer; öffnet bzw. schließt das Popover. */
  openPicker(): void {
    if (this.isDisabled) return;
    if (this.open) {
      this.closePicker(true);
      return;
    }
    this.attachPanel();
  }

  onHexInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.hexInput = raw;
    const trimmed = raw.trim();
    const digits = trimmed.replace(/^#/, '');
    // Nur vollständige Werte übernehmen (6 oder 8 Stellen) — beim Tippen
    // wären 3/4 Stellen sonst schon eine Kurzform.
    if ((digits.length === 6 || digits.length === 8) && parseHex(trimmed)) {
      const normalized = `#${digits.toLowerCase()}`;
      this.value = normalized;
      this.gradientType = null;
      this.panelRef?.setInput('value', normalized);
      this.onChange(normalized);
    }
    this.onTouched();
  }

  ngOnDestroy(): void {
    this.disposeOverlay();
  }

  private setValue(val: string) {
    this.value = val;
    const parsed = isGradient(val) ? parseColorValue(val) : null;
    this.gradientType = parsed?.kind === 'gradient' ? parsed.type : null;
    if (!this.gradientType) this.hexInput = val;
  }

  private attachPanel() {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(this.anchor)
          .withFlexibleDimensions(false)
          .withPush(true)
          .withViewportMargin(12)
          .withPositions([
            { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
            { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
            { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
          ]),
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'al-color-picker-pane',
      });
    }

    const portal = new ComponentPortal(ColorPickerPanelComponent, null, this.injector);
    this.panelRef = this.overlayRef.attach(portal);
    this.panelRef.setInput('allowGradient', this.allowGradient);
    this.panelRef.setInput('value', this.value);
    this.open = true;

    this.overlaySubs = new Subscription();
    this.overlaySubs.add(
      this.panelRef.instance.valueChange.subscribe((v: string) => {
        this.setValue(v);
        this.onChange(v);
        this.onTouched();
      })
    );
    this.overlaySubs.add(
      this.overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closePicker(true);
        }
      })
    );
    this.overlaySubs.add(
      this.overlayRef.outsidePointerEvents().subscribe((event) => {
        // Der Tupfer selbst schaltet um (openPicker) — nicht doppelt schließen.
        const target = event.target as Node | null;
        if (target && this.host.nativeElement.contains(target)) return;
        this.closePicker(false);
      })
    );
    this.panelRef.instance.focusFirst();
  }

  closePicker(returnFocus: boolean) {
    if (!this.open) return;
    this.overlaySubs.unsubscribe();
    this.overlayRef?.detach();
    this.panelRef = undefined;
    this.open = false;
    this.onTouched();
    if (returnFocus) this.swatch?.nativeElement.focus();
  }

  private disposeOverlay() {
    this.overlaySubs.unsubscribe();
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.panelRef = undefined;
    this.open = false;
  }
}
